import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { MapPin, Clock, Star, Truck, Search, Filter, X, Grid, List, Store as StoreIcon, Code } from 'lucide-react'
import { Store } from '../types'
import { api } from '../lib/api'
import toast from 'react-hot-toast'
import StoreImage from '../components/StoreImage'
import { calculateStoreIsOpen } from '../lib/storeUtils'
import { getCache, getCacheStale, setCache } from '../lib/cache'

// Función helper para truncar texto por cantidad de caracteres
// Basado en la longitud del mensaje de instrucciones del usuario (~80 caracteres)
const truncateDescription = (text: string, maxLength: number = 80): string => {
  if (!text || text.length <= maxLength) return text
  return text.substring(0, maxLength).trim() + '...'
}

const StoreSelection = () => {
  const [stores, setStores] = useState<Store[]>([])
  const [filteredStores, setFilteredStores] = useState<Store[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('')
  const [categories, setCategories] = useState<string[]>([])
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [showFilters, setShowFilters] = useState(false)

  // Ref para prevenir ejecuciones duplicadas (React StrictMode)
  const loadingStoresRef = useRef(false)

  useEffect(() => {
    // Prevenir ejecuciones duplicadas causadas por React StrictMode
    if (loadingStoresRef.current) return
    loadingStoresRef.current = true

    loadStores().finally(() => {
      loadingStoresRef.current = false
    })
  }, [])

  useEffect(() => {
    filterStores()
  }, [stores, searchTerm, selectedCategory])

  const loadStores = async () => {
    const cacheKey = 'store_selection_stores'
    
    // Intentar cargar desde cache primero (incluso si está expirado)
    const cachedStores = getCacheStale<Store[]>(cacheKey)
    const cachedData = getCache<Store[]>(cacheKey) // Solo datos válidos (no expirados)
    
    // Si hay cache válido (no expirado), mostrar y no hacer petición
    if (cachedData && cachedData.length > 0) {
      setStores(cachedData)
      const allCategories: string[] = cachedData.flatMap((store: Store) =>
        store.categories?.map((cat) => cat.name).filter((name) => name) || []
      )
      setCategories([...new Set(allCategories)])
      setLoading(false)
      // Si tenemos cache válido, no hacer query HTTP (evitar 304 innecesario)
      return
    }
    
    // Si hay cache expirado, mostrar esos datos mientras se actualiza
    if (cachedStores && cachedStores.length > 0) {
      setStores(cachedStores)
      const allCategories: string[] = cachedStores.flatMap((store: Store) =>
        store.categories?.map((cat) => cat.name).filter((name) => name) || []
      )
      setCategories([...new Set(allCategories)])
      // Continuar para actualizar en background
    }

    try {
      setLoading(true)
      const response = await api.getStores({ limit: 50 })
      if (response.success) {
        const mappedStores: Store[] = (response.data || []).map((storeData: any) => {
          // Calcular isActive basándose en operating_hours
          const isActive = storeData.is_active !== false
          const calculatedIsOpen = calculateStoreIsOpen(storeData.operating_hours, isActive)
          
          return {
            id: storeData.id,
            name: storeData.name,
            slug: storeData.slug,
            description: storeData.description,
            address: storeData.address,
            phone: storeData.phone,
            email: storeData.email || '',
            logoUrl: storeData.logo_url,
            coverImageUrl: storeData.cover_image_url,
            latitude: storeData.latitude,
            longitude: storeData.longitude,
            status: storeData.status,
            isActive: isActive && calculatedIsOpen, // Combinar is_active e is_open calculado
            offersKitchenService: storeData.offers_kitchen_service || false,
            offersDelivery: storeData.offers_delivery !== false,
            deliveryFee: Number(storeData.delivery_fee) || 0,
            minOrderAmount: Number(storeData.min_order_amount) || 0,
            deliveryTime: Number(storeData.delivery_time) || 30,
            operatingHours: storeData.operating_hours,
            chargesItbis: storeData.charges_itbis !== false,
            ownerId: storeData.owner_id || '',
            rating: storeData.rating,
            reviewCount: storeData.review_count || 0,
            categories: storeData.categories || [],
            developmentMode: storeData.development_mode === true
          }
        })
        
        setStores(mappedStores)
        const allCategories: string[] = mappedStores.flatMap((store: Store) =>
          store.categories?.map((cat) => cat.name).filter((name) => name) || []
        )
        setCategories([...new Set(allCategories)])
        
        // Guardar en cache (2 minutos de TTL para actualizaciones más frecuentes)
        setCache(cacheKey, mappedStores, 2 * 60 * 1000)
      }
    } catch (error: any) {
      console.error('Error loading stores:', error)
      
      // Si hay error de red y tenemos datos en cache, mantenerlos
      if ((error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.response) && cachedStores) {
        // Ya tenemos los datos del cache, no hacer nada más
        return
      }
      
      toast.error('Error al cargar los negocios')
    } finally {
      setLoading(false)
    }
  }

  const filterStores = () => {
    let filtered = stores

    if (searchTerm) {
      filtered = filtered.filter(store =>
        store.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        store.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        store.categories?.some(cat => cat.name.toLowerCase().includes(searchTerm.toLowerCase()))
      )
    }

    if (selectedCategory) {
      filtered = filtered.filter(store =>
        store.categories?.some(cat => cat.name === selectedCategory)
      )
    }

    setFilteredStores(filtered)
  }

  const clearFilters = () => {
    setSearchTerm('')
    setSelectedCategory('')
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 pb-20 sm:pb-6">
      {/* Header - iMac Style */}
      <div className="bg-white/90 backdrop-blur-2xl sticky top-0 z-40 shadow-lg border-b border-gray-200/60">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-6">
          <div className="text-center mb-3 sm:mb-6">
            <h1 className="text-xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-2 sm:mb-3 tracking-tight">
              Encuentra tu Negocio Ideal
            </h1>
            <p className="text-xs sm:text-lg text-gray-600 font-medium">
              Explora los mejores negocios de tu zona
            </p>
          </div>

          {/* Search Bar - iMac Style */}
          <div className="relative mb-3 sm:mb-5">
            <div className="absolute inset-y-0 left-0 pl-3 sm:pl-5 flex items-center pointer-events-none">
              <Search className="h-4 w-4 sm:h-6 sm:w-6 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Buscar negocios..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-10 sm:pl-14 pr-10 sm:pr-12 py-2.5 sm:py-5 bg-white border-2 border-gray-200 rounded-2xl sm:rounded-3xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 sm:focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-300 text-sm sm:text-lg shadow-sm hover:shadow-md"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute inset-y-0 right-0 pr-3 sm:pr-5 flex items-center text-gray-400 hover:text-gray-600 transition-colors active:scale-95"
              >
                <X className="h-4 w-4 sm:h-6 sm:w-6" />
              </button>
            )}
          </div>

          {/* Controls - iPhone Style */}
          <div className="flex items-center justify-between gap-3 sm:gap-4">
            <div className="flex items-center gap-2 flex-1">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl text-sm sm:text-base font-medium transition-all duration-200 active:scale-95 ${
                  showFilters || selectedCategory
                    ? 'bg-primary-600 text-white shadow-sm'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Filter className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="hidden sm:inline">Filtros</span>
              </button>
              {(searchTerm || selectedCategory) && (
                <button
                  onClick={clearFilters}
                  className="px-3 sm:px-4 py-2 sm:py-2.5 bg-gray-100 text-gray-700 rounded-xl text-sm sm:text-base font-medium hover:bg-gray-200 transition-all duration-200 active:scale-95 whitespace-nowrap"
                >
                  Limpiar
                </button>
              )}
            </div>
            <div className="flex items-center gap-1.5 bg-gray-100 p-1 rounded-xl">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 sm:p-2.5 rounded-lg transition-all duration-200 active:scale-95 ${
                  viewMode === 'grid' 
                    ? 'bg-white text-primary-600 shadow-sm' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Grid className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 sm:p-2.5 rounded-lg transition-all duration-200 active:scale-95 ${
                  viewMode === 'list' 
                    ? 'bg-white text-primary-600 shadow-sm' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <List className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            </div>
          </div>

          {/* Filters Panel - iMac Style */}
          {showFilters && (
            <div className="mt-3 sm:mt-5 p-3 sm:p-6 bg-white/95 backdrop-blur-xl rounded-2xl sm:rounded-3xl border-2 border-gray-200/60 shadow-xl animate-fade-in">
              <div className="flex items-center justify-between mb-3 sm:mb-5">
                <h3 className="text-sm sm:text-xl font-bold text-gray-900">Filtros</h3>
                <button
                  onClick={clearFilters}
                  className="text-xs sm:text-base font-semibold text-primary-600 hover:text-primary-700 active:scale-95 transition-all px-2 sm:px-3 py-1 sm:py-1.5 rounded-xl hover:bg-primary-50"
                >
                  Limpiar
                </button>
              </div>
              <div className="space-y-3 sm:space-y-5">
                <div>
                  <label className="block text-xs sm:text-base font-semibold text-gray-700 mb-2 sm:mb-3">Categoría</label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="w-full px-3 sm:px-5 py-2.5 sm:py-4 text-sm sm:text-base bg-white border-2 border-gray-200 rounded-xl sm:rounded-2xl focus:ring-2 sm:focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 transition-all shadow-sm hover:shadow-md"
                  >
                    <option value="">Todas las categorías</option>
                    {categories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center justify-center pt-2 sm:pt-3 text-xs sm:text-base text-gray-600 font-medium">
                  <MapPin className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
                  <span className="text-center">Mostrando negocios en Santo Domingo y alrededores</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Stores - iPhone Style */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6">
        {/* Results Count */}
        {!loading && (
          <div className="mb-3 sm:mb-6">
            <p className="text-xs sm:text-base text-gray-600 font-medium">
              {filteredStores.length} negocio{filteredStores.length !== 1 ? 's' : ''} encontrado{filteredStores.length !== 1 ? 's' : ''}
            </p>
          </div>
        )}

        {loading ? (
          <div className={viewMode === 'grid' 
            ? 'grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4' 
            : 'space-y-3 sm:space-y-4'
          }>
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <div
                key={i}
                className={`bg-white rounded-2xl sm:rounded-3xl shadow-sm border border-gray-200/50 overflow-hidden animate-pulse ${
                  viewMode === 'list' ? 'flex gap-3 sm:gap-4 p-3 sm:p-4' : 'flex flex-col'
                }`}
              >
                <div className={viewMode === 'grid' ? 'aspect-square bg-gray-200' : 'w-24 h-24 sm:w-32 sm:h-32 flex-shrink-0 bg-gray-200 rounded-xl'}></div>
                <div className={`flex-1 ${viewMode === 'grid' ? 'p-3 sm:p-4' : 'py-2'}`}>
                  <div className="h-4 sm:h-5 bg-gray-200 rounded mb-2 sm:mb-3"></div>
                  <div className="h-3 sm:h-4 bg-gray-200 rounded mb-2 w-2/3"></div>
                  <div className="h-3 sm:h-4 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        ) : filteredStores.length === 0 ? (
          <div className="text-center py-12 sm:py-24">
            <div className="bg-white/95 backdrop-blur-xl rounded-2xl sm:rounded-3xl p-6 sm:p-14 max-w-lg mx-auto shadow-2xl border-2 border-gray-200/60">
              <StoreIcon className="mx-auto h-12 w-12 sm:h-20 sm:w-20 text-gray-300 mb-4 sm:mb-6" />
              <h3 className="text-lg sm:text-3xl font-bold text-gray-900 mb-2 sm:mb-3">No se encontraron negocios</h3>
              <p className="text-xs sm:text-lg text-gray-600 mb-6 sm:mb-8 font-medium">Intenta ajustar los filtros de búsqueda</p>
              <button
                onClick={clearFilters}
                className="px-6 sm:px-8 py-2.5 sm:py-4 bg-primary-600 text-white rounded-xl sm:rounded-2xl font-bold hover:bg-primary-700 active:scale-95 transition-all duration-200 shadow-lg hover:shadow-xl text-sm sm:text-lg"
              >
                Limpiar Filtros
              </button>
            </div>
          </div>
        ) : (
          <div className={viewMode === 'grid' 
            ? 'grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4' 
            : 'space-y-3 sm:space-y-4'
          }>
            {filteredStores.map((store) => (
              <Link
                key={store.id}
                to={`/stores/${store.id}`}
                className={`bg-white rounded-2xl sm:rounded-3xl shadow-lg border-2 border-gray-200/60 overflow-hidden hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 cursor-pointer active:scale-[0.97] group ${
                  viewMode === 'list' ? 'flex gap-3 sm:gap-5 p-3 sm:p-5' : 'flex flex-col'
                }`}
              >
                {viewMode === 'grid' ? (
                  <>
                    <div className="relative aspect-square bg-gradient-to-br from-gray-50 to-gray-100 overflow-hidden group-hover:scale-105 transition-transform duration-300">
                      <StoreImage
                        imageKey={store.coverImageUrl || null}
                        type="cover"
                        className="w-full h-full object-cover"
                        alt={store.name}
                        storeId={store.id}
                      />
                      {/* Status Badge */}
                      {store.isActive ? (
                        <div className="absolute top-1.5 left-1.5 sm:top-3 sm:left-3 bg-green-500 text-white px-2 py-0.5 sm:px-3 sm:py-1.5 rounded-full text-[10px] sm:text-sm font-bold shadow-xl backdrop-blur-sm">
                          Abierto
                        </div>
                      ) : (
                        <div className="absolute top-1.5 left-1.5 sm:top-3 sm:left-3 bg-red-500 text-white px-2 py-0.5 sm:px-3 sm:py-1.5 rounded-full text-[10px] sm:text-sm font-bold shadow-xl backdrop-blur-sm">
                          Cerrado
                        </div>
                      )}
                      {/* Development Mode Badge */}
                      {store.developmentMode && (
                        <div className="absolute top-1.5 right-1.5 sm:top-3 sm:right-3 bg-amber-500 text-white px-2 py-0.5 sm:px-3 sm:py-1.5 rounded-full text-[10px] sm:text-sm font-bold shadow-xl backdrop-blur-sm flex items-center space-x-1 sm:space-x-1.5 z-10">
                          <Code className="w-3 h-3 sm:w-4 sm:h-4" />
                          <span>DEV</span>
                        </div>
                      )}
                      {/* Rating Badge */}
                      {store.rating != null && typeof store.rating === 'number' && (
                        <div className={`absolute ${store.developmentMode ? 'top-1.5 right-1.5 sm:top-3 sm:right-3 mt-6 sm:mt-8' : 'top-1.5 right-1.5 sm:top-3 sm:right-3'} bg-white/95 backdrop-blur-md px-2 py-0.5 sm:px-3 sm:py-1.5 rounded-full shadow-xl border border-gray-200/50`}>
                          <div className="flex items-center space-x-1 sm:space-x-1.5">
                            <Star className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-400 fill-current" />
                            <span className="text-[10px] sm:text-sm font-bold text-gray-900">
                              {store.rating.toFixed(1)}
                            </span>
                          </div>
                        </div>
                      )}
                      {/* Logo overlay */}
                      <div className="absolute bottom-1.5 left-1.5 sm:bottom-3 sm:left-3">
                        <div className="w-10 h-10 sm:w-20 sm:h-20 rounded-xl sm:rounded-2xl bg-white shadow-2xl border-2 sm:border-4 border-white overflow-hidden flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                          <StoreImage
                            imageKey={store.logoUrl || null}
                            type="logo"
                            className="w-full h-full object-cover"
                            alt={store.name}
                            storeId={store.id}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="p-2.5 sm:p-5 flex flex-col flex-1">
                      <div className="flex items-start justify-between gap-2 mb-1 sm:mb-2">
                        <h3 className="font-bold text-xs sm:text-lg text-gray-900 line-clamp-2 leading-tight group-hover:text-primary-600 transition-colors flex-1">{store.name}</h3>
                        {store.developmentMode && (
                          <span className="bg-amber-500 text-white px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-full text-[9px] sm:text-xs font-bold flex items-center gap-1 flex-shrink-0">
                            <Code className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                            <span className="hidden sm:inline">DEV</span>
                          </span>
                        )}
                      </div>
                      {store.description && (
                        <p className="text-[10px] sm:text-base text-gray-600 mb-2 sm:mb-3 leading-relaxed hidden sm:block">{truncateDescription(store.description)}</p>
                      )}
                      <div className="flex items-center text-[10px] sm:text-base text-gray-600 mb-2 sm:mb-3 font-medium">
                        <MapPin className="w-3 h-3 sm:w-5 sm:h-5 mr-1 sm:mr-2 flex-shrink-0 text-primary-500" />
                        <span className="truncate text-[10px] sm:text-base">{store.address}</span>
                      </div>
                      <div className="flex items-center justify-between mb-2 sm:mb-3 mt-auto">
                        <div className="flex items-center space-x-1 sm:space-x-2 text-[10px] sm:text-base text-gray-700 font-semibold">
                          <Clock className="w-3 h-3 sm:w-5 sm:h-5 text-primary-500" />
                          <span>{store.deliveryTime || '25-35'} min</span>
                        </div>
                        <div className="flex items-center space-x-1 sm:space-x-2 text-[10px] sm:text-base text-gray-700 font-semibold">
                          <Truck className="w-3 h-3 sm:w-5 sm:h-5 text-primary-500" />
                          <span className="text-primary-600">${store.deliveryFee === 0 ? 'Gratis' : store.deliveryFee}</span>
                        </div>
                      </div>
                      {store.categories && store.categories.length > 0 && (
                        <div className="flex flex-wrap gap-1 sm:gap-2 mt-auto">
                          {store.categories.slice(0, 2).map((category) => (
                            <span
                              key={category.name}
                              className="bg-gradient-to-r from-primary-50 to-primary-100 text-primary-700 px-1.5 py-0.5 sm:px-3 sm:py-1 rounded-full text-[9px] sm:text-sm font-semibold border border-primary-200/50"
                            >
                              {category.name}
                            </span>
                          ))}
                          {(store.categories.length || 0) > 2 && (
                            <span className="text-[9px] sm:text-sm text-gray-500 px-1.5 sm:px-3 py-0.5 sm:py-1 font-medium">
                              +{(store.categories.length || 0) - 2}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="relative w-24 h-24 sm:w-32 sm:h-32 flex-shrink-0 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl sm:rounded-2xl overflow-hidden">
                      <StoreImage
                        imageKey={store.coverImageUrl || null}
                        type="cover"
                        className="w-full h-full object-cover"
                        alt={store.name}
                        storeId={store.id}
                      />
                      {/* Logo overlay */}
                      <div className="absolute bottom-1 left-1">
                        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-white shadow-lg border border-white overflow-hidden">
                          <StoreImage
                            imageKey={store.logoUrl || null}
                            type="logo"
                            className="w-full h-full object-cover"
                            alt={store.name}
                            storeId={store.id}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="flex-1 min-w-0 py-1 sm:py-2">
                      <div className="flex items-start justify-between mb-1 sm:mb-2">
                        <h3 className="font-semibold text-xs sm:text-base lg:text-lg text-gray-900 line-clamp-1 flex-1">{store.name}</h3>
                        <div className="flex items-center gap-1 sm:gap-2 ml-2 flex-shrink-0">
                          {store.developmentMode && (
                            <span className="bg-amber-500 text-white px-1.5 py-0.5 sm:px-2 sm:py-0.5 rounded-full text-[9px] sm:text-xs font-bold flex items-center gap-1">
                              <Code className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                              <span>DEV</span>
                            </span>
                          )}
                          {store.isActive ? (
                            <span className="bg-green-500 text-white px-1.5 py-0.5 sm:px-2 sm:py-0.5 rounded-full text-[9px] sm:text-xs font-semibold">
                              Abierto
                            </span>
                          ) : (
                            <span className="bg-red-500 text-white px-1.5 py-0.5 sm:px-2 sm:py-0.5 rounded-full text-[9px] sm:text-xs font-semibold">
                              Cerrado
                            </span>
                          )}
                          {store.rating != null && typeof store.rating === 'number' && (
                            <div className="flex items-center space-x-0.5 sm:space-x-1 bg-white/90 backdrop-blur-sm px-1.5 py-0.5 sm:px-2 sm:py-0.5 rounded-full shadow-sm">
                              <Star className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-yellow-400 fill-current" />
                              <span className="text-[9px] sm:text-xs font-semibold text-gray-900">
                                {store.rating.toFixed(1)}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                      {store.description && (
                        <p className="text-[10px] sm:text-sm text-gray-500 mb-1 sm:mb-2 leading-relaxed hidden sm:block">{truncateDescription(store.description)}</p>
                      )}
                      <div className="flex items-center text-[10px] sm:text-sm text-gray-500 mb-1 sm:mb-2">
                        <MapPin className="w-2.5 h-2.5 sm:w-4 sm:h-4 mr-0.5 sm:mr-1 flex-shrink-0" />
                        <span className="truncate text-[10px] sm:text-sm">{store.address}</span>
                      </div>
                      <div className="flex items-center justify-between text-[10px] sm:text-sm text-gray-600 mb-1 sm:mb-2">
                        <div className="flex items-center space-x-0.5 sm:space-x-1">
                          <Clock className="w-2.5 h-2.5 sm:w-4 sm:h-4" />
                          <span>{store.deliveryTime || '25-35'} min</span>
                        </div>
                        <div className="flex items-center space-x-0.5 sm:space-x-1">
                          <Truck className="w-2.5 h-2.5 sm:w-4 sm:h-4" />
                          <span>${store.deliveryFee === 0 ? 'Gratis' : store.deliveryFee}</span>
                        </div>
                        {store.minOrderAmount > 0 && (
                          <span className="text-[9px] sm:text-xs text-gray-500 hidden sm:inline">
                            Mín: ${store.minOrderAmount}
                          </span>
                        )}
                      </div>
                      {store.categories && store.categories.length > 0 && (
                        <div className="flex flex-wrap gap-1 sm:gap-1.5">
                          {store.categories.slice(0, 3).map((category) => (
                            <span
                              key={category.name}
                              className="bg-primary-50 text-primary-600 px-1.5 py-0.5 sm:px-2 sm:py-0.5 rounded-full text-[9px] sm:text-xs font-medium"
                            >
                              {category.name}
                            </span>
                          ))}
                          {(store.categories.length || 0) > 3 && (
                            <span className="text-[9px] sm:text-xs text-gray-500 px-1.5 sm:px-2 py-0.5">
                              +{(store.categories.length || 0) - 3} más
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default StoreSelection
